import { Component } from "react"
import { connect } from "react-redux"
import { matchPath, withRouter } from "react-router-dom"
import isEqual from "lodash/isEqual"

import Rmbx from "../util"
import LoadingIndicator from "../components/loading-indicator"
import { initializedUser } from "../actions/auth"
import { get_current_user } from "../actions/index"
import { loadAllEntities } from "../cached-data/actions"
import { fetchEmployeeStartOfWeek } from "../filters/actions"
import { companyOptionsSelector, currentProjectIdSelector, currentProjectSelector, isFeatureEnabledSelector, userInitializedSelector, userSelector } from "../selectors"
import SidebarWrapper from "../components/navigation/sidebar-wrapper"
import { ConnectedRouteList } from "./routeList"
import { routesWithoutSidebar } from "./routes"
import featureFlagsClient from "../featureFlagsClient"
import { deleteCookie, featureFlagClientIdentifyUser } from "../common/ts-utils"
import TrialAccountInfoBar from "../components/trials/trial-account-info-bar"
import { getAuthToken } from "../util"
import { getFlagEnabled } from "../getFlagValue"
import { getUserHasProjects } from "../api/get"
import { FeatureFlagsReadyHook } from "../FeatureFlagsReadyHook"

const _openRoutes = [
    "/rhumbix/guest_login/*",
    "/rhumbix/login/",
    "/rhumbix/login/lockout/",
    "/rhumbix/external-login/",
    "/rhumbix/password/forgot/",
    "/rhumbix/password/set/",
    "/rhumbix/password/reset/*",
    "/rhumbix/password/set-new/*",
    "/rhumbix/trial/deactivated/",
]

const _isOpenUrl = path => {
    for (const url of ["login/lockout", "guest_login", "guest_access", "password/reset", "password/set-new", "shared", "sign", "trial/deactivated"]) {
        if (path.startsWith(`/rhumbix/${url}/`)) {
            return true
        }
    }
    return false
}

// The Autodesk company-setup flow (WA-9395) is reached with a valid token but, for a
// brand-new Team Admin, no Employee/company. It must NOT run the authenticated app
// bootstrap (get_current_user / loadAllEntities), which assumes a company and would
// 403/401 (logging the user out) and leave AuthProvider stuck on the loading screen.
// These routes render their own standalone page using the stored token.
//
// autodesk-license-error (WA-9424) shares this gate for a different reason: it is
// the page get_current_user's 403 (NO_AUTODESK_LICENSE / AUTODESK_LICENSE_UNAVAILABLE)
// navigates to. Without this gate, redirect()'s ssoLoginError check would immediately
// clear the session and bounce back to /rhumbix/login/, where isAutodeskLoginOnly would
// re-trigger the Autodesk popup with no explanation ever shown — the original bug.
const _autodeskProvisioningRoutes = ["/rhumbix/autodesk-setup", "/rhumbix/autodesk-no-company", "/rhumbix/autodesk-license-error"]

const _isAutodeskProvisioningRoute = path => _autodeskProvisioningRoutes.some(route => path === route || path === `${route}/`)

/**
 * A React component that determines the user's auth status, redirects the user
 * (if necessary), and delays rendering the app for authenticated users until
 * at least one page of projects has been loaded.
 *
 * While the data is loading, nothing is rendered.
 */
class AuthProvider extends Component {
    constructor(props) {
        super(props)
        this.state = {
            currentMenuGroup: "",
            // Pre-authorize provisioning routes immediately (token existence is the gate;
            // the backend enforces IsAutodeskProvisioningSession on all endpoints). Without
            // this, isAuthorized stays false until ProtectedRoute's useEffect fires, which
            // calls updateIsAuthorized(true) → setState → re-render → RouteList remount
            // → new ProtectedRoute calls updateIsAuthorized(true) again → cascade loop.
            isAuthorized: _isAutodeskProvisioningRoute(props.location?.pathname) && !!getAuthToken(),
            hasCheckedForProjects: false,
            userHasProjects: false,
            featureFlagsReady: false,
        }
    }

    redirectToLogin = (redirectRoutes, currentPath) => {
        if (!redirectRoutes.includes(currentPath)) {
            Rmbx.store.set("attempted_url", currentPath)
        }

        if (currentPath !== "/rhumbix/login/") {
            this.clearSessionData()
            Rmbx.util.history.push(`/rhumbix/login/${this.props.location.search}`)
        }
    }

    clearSessionData = () => {
        const attemptedUrl = Rmbx.store.get("attempted_url")
        Rmbx.util.destroySession()
        deleteCookie("token")
        deleteCookie("authorization_type")
        if (attemptedUrl) {
            Rmbx.store.set("attempted_url", attemptedUrl)
        }
    }

    setDefaultFiltersAndRedirect = (attemptedUrl, redirectRoutes, currentPath) => {
        this.props.initializedUser()

        if (!this.state.userHasProjects) {
            // Project list was empty -- redirect to project setup
            Rmbx.util.history.push("/rhumbix/projects/")
        } else {
            this.redirectToLandingPage(attemptedUrl, redirectRoutes, currentPath)
        }
    }

    redirectToLandingPage = (attemptedUrl, redirectRoutes, currentPath) => {
        if (redirectRoutes.includes(currentPath)) {
            if (attemptedUrl) {
                Rmbx.util.history.push(attemptedUrl)
                Rmbx.store.set("attempted_url", null)
            } else {
                let redirectUrl = "/rhumbix/projects" // Fallback URL

                if (this.props.isTimekeepingFeatureEnabled) {
                    redirectUrl = "/rhumbix/time-cards/weekly/"
                } else if (typeof this.props.isTimekeepingFeatureEnabled !== "undefined" && this.props.currentUser?.list_views?.length) {
                    /**
                     * The reason we have to check if the flag is undefined here is to avoid assigning
                     * '/rhumbix/field-forms/t-and-m-tracking/ to history before feature flags are loaded in
                     * store->state If we did that, since the t-and-m-tracking route is not in
                     * the redirectRoutes array we would never pass the first if check even when the state was
                     * updated successfully, and if we try adding it to
                     * the redirectRoutes array we break A LOT of things
                     */
                    const currentUserListViews = this.props.currentUser.list_views

                    // If show_nav_link is false, navigation will fail.
                    // Arbitrarily find the first list view where
                    // this property is true
                    const validListView = currentUserListViews.find(lv => lv.show_nav_link)

                    if (validListView) redirectUrl = `/rhumbix/field-forms/list-view/${validListView.slug}`
                }

                Rmbx.util.history.push(redirectUrl)
            }
        }
    }

    redirect = () => {
        const currentPath = this.props.location.pathname
        // A company-scoped 403 caused by Autodesk licensing (no seat, or the licensing
        // service unreachable) must not fall through to the generic ssoLoginError branch
        // below, which would clear the session and bounce to /rhumbix/login/ — where
        // isAutodeskLoginOnly would re-trigger the Autodesk popup with no explanation
        // ever shown (WA-9424, the original infinite-popup bug). Route to the dead-end
        // page instead, and stay there once arrived (this branch re-runs on every
        // redirect() call while the error state is set, so it must be a no-op once
        // currentPath is already the error page).
        if (this.props.autodeskLicenseErrorType) {
            const errorRoute = "/rhumbix/autodesk-license-error"
            if (currentPath !== errorRoute && currentPath !== `${errorRoute}/`) {
                Rmbx.util.history.push(errorRoute)
            }
            return
        }
        // The Autodesk setup flow stays put for an authenticated (ADSK-session) user —
        // no redirect to the app/landing page. A visitor with no token is still sent to
        // login (defence-in-depth; the backend endpoints separately require an
        // AutodeskSession, so the data is never exposed regardless of this UI gate).
        if (_isAutodeskProvisioningRoute(currentPath)) {
            if (!getAuthToken()) {
                this.redirectToLogin(["/rhumbix/", "/rhumbix/login/"], currentPath)
            }
            return
        }
        const token = getAuthToken()

        const isOpenRoute = _openRoutes.includes(currentPath)
        const isOpenUrl = _isOpenUrl(currentPath)
        const redirectRoutes = ["/rhumbix/", "/rhumbix/login/"]
        const attemptedUrl = Rmbx.store.get("attempted_url")

        if (this.props.ssoLoginError) {
            this.redirectToLogin(redirectRoutes, currentPath)
        } else if (!token && !isOpenRoute && !isOpenUrl) {
            this.redirectToLogin(redirectRoutes, currentPath)
        } else if (token && !this.props.userInitialized && this.state.hasCheckedForProjects) {
            this.setDefaultFiltersAndRedirect(attemptedUrl, redirectRoutes, currentPath)
        } else if (token) {
            this.redirectToLandingPage(attemptedUrl, redirectRoutes, currentPath)
        }
    }

    loadData() {
        const token = getAuthToken()
        if (!this.state.featureFlagsReady) {
            return
        }

        if (_isAutodeskProvisioningRoute(this.props.location.pathname)) {
            this.redirect()
            // Run get_current_user so users who already have an employee/company (e.g. an
            // existing admin adding a second company) get their full current_user in Redux and
            // therefore see the correct sidebar — company navigation, "Add a Company", etc.
            //
            // Employee-less users (brand-new Team Admins) will 403 on /api/v3/users/me.
            // That sets ssoLoginError, but redirect() exempts provisioning routes from the
            // ssoLoginError logout redirect, so they land safely in the wizard.
            //
            // Guards:
            // - !ssoLoginError: avoids retrying after a 403 (employee-less user).
            // - !userInitialized: prevents repeated calls on every componentDidUpdate.
            //   initializedUser() is called in finally so it fires on both success and failure.
            if (token && !this.props.ssoLoginError && !this.props.userInitialized) {
                this.props.getCurrentUser().finally(() => {
                    this.props.initializedUser()
                })
            }
            return
        }

        let checkProjectsPromise = Promise.resolve()
        if (token && !this.state.hasCheckedForProjects) {
            checkProjectsPromise = getUserHasProjects()
                .then(userHasProjects => {
                    this.setState({ hasCheckedForProjects: true, userHasProjects })
                })
                .catch(() => {
                    this.setState({ hasCheckedForProjects: true, userHasProjects: false })
                })
        }

        if (token && !this.props.ssoLoginError && !this.props.userInitialized) {
            this.props
                .getCurrentUser()
                .then(() => {
                    const { currentUser } = this.props
                    if (currentUser?.employee_id) {
                        featureFlagClientIdentifyUser(currentUser, featureFlagsClient)
                        this.props.fetchEmployeeStartOfWeek(currentUser.employee_id)
                    }
                    this.props.loadAllEntities("timekeepingStatuses")
                    if (currentUser?.features?.work_rules) {
                        this.props.loadAllEntities("workRuleSets")
                    }
                })
                .then(() => checkProjectsPromise)
                .finally(() => {
                    this.redirect()
                })
        } else {
            this.redirect()
        }
    }

    componentDidMount() {
        this.loadData()
    }

    componentDidUpdate(prevProps, prevState) {
        if (!isEqual(this.props, prevProps) || !isEqual(this.state.featureFlagsReady, prevState.featureFlagsReady)) {
            this.loadData()
        }
    }

    updateCurrentMenuGroup = menuGroup => {
        if (menuGroup) {
            this.setState({ currentMenuGroup: menuGroup })
        }
    }

    /**
     * Update "isAuthorized" state, use this to show/hide sidebar.
     * Idempotent: skips setState when the value hasn't changed, which prevents a
     * re-render → RouteList-remount → ProtectedRoute-effect → setState cascade.
     */
    updateIsAuthorized = value => {
        if (this.state.isAuthorized !== value) {
            this.setState({ isAuthorized: value })
        }
    }

    isTrialAccount = () => !!this.props.deactivationDate && this.props.companyType && (this.props.companyType === "FREE_TRIAL" || this.props.companyType === "PAID_TRIAL")

    render() {
        const token = getAuthToken()
        // The Autodesk setup routes render immediately (no authenticated bootstrap to wait on).
        const isProvisioningRoute = _isAutodeskProvisioningRoute(this.props.location.pathname)
        const isDataLoading = !isProvisioningRoute && token && !this.props.ssoLoginError && (!this.props.userInitialized || !this.state.hasCheckedForProjects)

        if (isDataLoading || !this.state.featureFlagsReady) {
            return (
                <>
                    <LoadingIndicator active={true} />
                    <FeatureFlagsReadyHook onReady={() => this.setState({ featureFlagsReady: true })} />
                </>
            )
        }

        const renderSidebar =
            !routesWithoutSidebar.some(routePattern => matchPath(this.props.location.pathname, routePattern)) &&
            !(
                // Couldn't use routesWithSidebar because matchPath
                // is unhappy with the special characters in the pathname
                // for guest form shares, most likely the colons.
                (getFlagEnabled("WA-7861-prevent-guest-mode-login-loop") && (this.props.location.pathname.includes("/guest_login/") || this.props.location.pathname.includes("/guest_access/")))
            )

        return (
            <>
                {this.isTrialAccount() ? <TrialAccountInfoBar deactivationDate={this.props.deactivationDate} /> : null}
                <div className="sidebar-content-viewport-container">
                    {renderSidebar && this.state.isAuthorized ? <SidebarWrapper currentMenuGroup={this.state.currentMenuGroup} /> : null}
                    <ConnectedRouteList updateMenuGroup={this.updateCurrentMenuGroup} updateIsAuthorized={this.updateIsAuthorized} />
                </div>
            </>
        )
    }
}

const mapStateToProps = state => ({
    currentProjectId: currentProjectIdSelector(state),
    currentProject: currentProjectSelector(state),
    currentUser: userSelector(state),
    isTimekeepingFeatureEnabled: isFeatureEnabledSelector(state, "timekeeping"),
    userInitialized: userInitializedSelector(state),
    ssoLoginError: state.current_user_failed && state.current_user_failed.errorResponseData,
    autodeskLicenseErrorType: state.auth.autodeskLicenseErrorType,
    deactivationDate: companyOptionsSelector(state)?.deactivation_date ?? undefined,
    companyType: companyOptionsSelector(state)?.company_type ?? undefined,
})

const mapDispatchToProps = {
    getCurrentUser: get_current_user,
    initializedUser: initializedUser,
    loadAllEntities,
    fetchEmployeeStartOfWeek,
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AuthProvider))
